import Button, { ButtonProps } from 'components/UI/V3/Button/Button';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownContentProps } from 'components/UI/V3/Dropdown/Content/DropdownContent';
import { DropdownFooter } from 'components/UI/V3/Dropdown/Footer/DropdownFooter';
import { DropdownHeader } from 'components/UI/V3/Dropdown/Header/DropdownHeader';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import React from 'react';

export type SelectDropdownOption<T> = {
    id: T;
    label: string;
};

type BaseProps<T> = {
    label?: string;
    showSelectAll?: boolean;
    options: Array<SelectDropdownOption<T>>;

    trigger?: React.ReactNode;

    buttonProps?: Partial<ButtonProps>;

    dropdownProps?: Partial<DropdownContentProps>;
};

type SingleValueProps<T> = BaseProps<T> & {
    multiple?: false;
    value: SelectDropdownOption<T> | null;
    onChange?: (value: SelectDropdownOption<T> | null) => void;
};

type MultipleValueProps<T> = BaseProps<T> & {
    multiple: true;
    value: SelectDropdownOption<T>[];
    onChange?: (value: SelectDropdownOption<T>[]) => void;
};

type SelectDropdownProps<T> = SingleValueProps<T> | MultipleValueProps<T>;

export const SelectDropdown = <T,>({
    label,
    options,
    multiple,
    showSelectAll,
    value,
    trigger,
    buttonProps,
    dropdownProps,
    onChange
}: SelectDropdownProps<T>) => {
    const safeOptions = Array.isArray(options) ? options : [];

    const [isOpen, setIsOpen] = React.useState(false);
    const [selectedOptions, setSelectedOptions] = React.useState<Array<SelectDropdownOption<T>>>(() => {
        if (multiple && Array.isArray(value)) {
            return value;
        }

        return safeOptions;
    });

    React.useEffect(() => {
        if (!multiple || !Array.isArray(value)) {
            return;
        }

        setSelectedOptions(value.filter(selected => safeOptions.some(option => option.id === selected.id)));
    }, [multiple, value, safeOptions]);

    const isAllSelected = React.useMemo(() => {
        return selectedOptions.length === safeOptions.length && safeOptions.length > 0;
    }, [selectedOptions, safeOptions]);

    const buttonLabel = React.useMemo(() => {
        if (!Array.isArray(value)) {
            return value?.label;
        }

        if (selectedOptions.length === options.length) {
            return `All ${label}`;
        }

        return `(${selectedOptions.length}) ${label}`;
    }, [value, label]);

    const triggerElement = React.useMemo(() => {
        if (trigger) {
            return trigger;
        }

        return (
            <Button
                label={buttonLabel}
                {...buttonProps}
            />
        );
    }, [selectedOptions, buttonLabel, buttonProps, trigger]);

    const isOptionSelected = (option: SelectDropdownOption<T>): boolean => {
        if (!multiple) {
            return value?.id === option.id;
        }

        return selectedOptions.some(selected => selected.id === option.id);
    };

    const renderOptionItem = (option: SelectDropdownOption<T>) => {
        const isSelected = isOptionSelected(option);

        return (
            <DropdownItem
                key={option.label}
                checked={isSelected}
                type={multiple ? 'checkbox' : 'radio'}
                onSelect={checked => optionSelectHandler(option, checked)}
            >
                {option.label}
            </DropdownItem>
        );
    };

    const selectAllHandler = (checked: boolean) => {
        if (checked) {
            setSelectedOptions(safeOptions);
        } else {
            setSelectedOptions([]);
        }
    };

    const optionSelectHandler = (newValue: SelectDropdownOption<T>, checked: boolean) => {
        if (!multiple) {
            setIsOpen(false);
            return onChange?.(newValue);
        }

        const newSelection = checked
            ? [...selectedOptions, newValue]
            : selectedOptions.filter(v => v.id !== newValue.id);

        setSelectedOptions(newSelection);
    };

    const applyHandler = () => {
        setIsOpen(false);

        if (multiple) {
            onChange?.(selectedOptions);
        } else {
            onChange?.(selectedOptions[0]);
        }
    };

    return (
        <Dropdown
            open={isOpen}
            onOpenChange={setIsOpen}
        >
            <DropdownTrigger>{triggerElement}</DropdownTrigger>

            <DropdownContent {...dropdownProps}>
                {multiple && showSelectAll && safeOptions.length > 0 && (
                    <DropdownHeader>
                        <DropdownItem
                            type="checkbox"
                            checked={isAllSelected}
                            onSelect={selectAllHandler}
                        >
                            All {label}
                        </DropdownItem>
                    </DropdownHeader>
                )}

                {safeOptions.map(renderOptionItem)}

                {multiple && (
                    <DropdownFooter>
                        <Button
                            label="Apply"
                            variant="green"
                            disabled={selectedOptions.length === 0}
                            onClick={applyHandler}
                        />
                    </DropdownFooter>
                )}
            </DropdownContent>
        </Dropdown>
    );
};
